import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeColors, ThemeMode } from '@library/stylesheets/theme';
import { palettes } from './palette';

// Three-state theme, matching the phone apps' ThemeID contract:
//   0 = System, 1 = Dark, 2 = Light
// (see the Themes table / busLineApp ThemeProvider). Keeping the same numbering
// means a user's stored ThemeID could drive this later without a translation table.
export const THEME_SYSTEM = 0;
export const THEME_DARK = 1;
export const THEME_LIGHT = 2;

export type ThemeID = typeof THEME_SYSTEM | typeof THEME_DARK | typeof THEME_LIGHT;

const THEME_KEY = 'bgweb_theme_id';

// Every ThemeColors token becomes a CSS custom property, so the stylesheet can use
// var(--c-app-background) etc. and palette.ts stays the single source of truth —
// no colour is written twice.
const cssVarName = (token: string) => `--c-${token.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`;

const applyPalette = (colors: ThemeColors, mode: ThemeMode): void => {
    const root = document.documentElement;
    (Object.keys(colors) as Array<keyof ThemeColors>).forEach((token) => {
        root.style.setProperty(cssVarName(token), colors[token]);
    });
    // Lets CSS branch on the resolved mode, and tells the browser to theme its own
    // chrome (scrollbars, form controls) to match.
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
};

// Call once, synchronously, before React mounts. The provider applies the palette
// from an effect, which runs after the first paint — without this the very first
// frame would render with no custom properties set (unstyled flash).
export const applyInitialTheme = (): void => {
    const id = readStoredThemeID();
    const mode: ThemeMode = id === THEME_SYSTEM
        ? systemMode()
        : id === THEME_DARK ? 'dark' : 'light';
    applyPalette(palettes[mode], mode);
};

interface ThemeContextValue {
    /** The user's choice, including "follow the system". */
    themeID: ThemeID;
    /** What that choice actually resolves to right now. */
    mode: ThemeMode;
    colors: ThemeColors;
    setThemeID: (id: ThemeID) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const readStoredThemeID = (): ThemeID => {
    try {
        const raw = window.localStorage.getItem(THEME_KEY);
        const parsed = Number(raw);
        if (parsed === THEME_DARK || parsed === THEME_LIGHT || parsed === THEME_SYSTEM) return parsed;
    } catch { /* ignore */ }
    return THEME_SYSTEM;
};

const systemMode = (): ThemeMode =>
    window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [themeID, setThemeIDState] = useState<ThemeID>(readStoredThemeID);
    const [systemPref, setSystemPref] = useState<ThemeMode>(systemMode);

    // Keep following the OS while the choice is "System" — a user switching their
    // machine to dark at dusk should see this follow without a reload.
    useEffect(() => {
        const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (!mq) return;
        const onChange = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const mode: ThemeMode = themeID === THEME_SYSTEM
        ? systemPref
        : themeID === THEME_DARK ? 'dark' : 'light';

    const colors = palettes[mode];

    useEffect(() => {
        applyPalette(colors, mode);
    }, [colors, mode]);

    const setThemeID = useCallback((id: ThemeID) => {
        setThemeIDState(id);
        try {
            window.localStorage.setItem(THEME_KEY, String(id));
        } catch { /* ignore */ }
    }, []);

    const value = useMemo<ThemeContextValue>(
        () => ({ themeID, mode, colors, setThemeID }),
        [themeID, mode, colors, setThemeID],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
